import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { FolderOpen, Upload, Download, Trash2, FileText, File, FileSpreadsheet, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

const CATEGORIES = [
  { value: 'reglamento', label: 'Reglamento' },
  { value: 'actas', label: 'Actas' },
  { value: 'estados_cuenta', label: 'Estados de Cuenta' },
  { value: 'circulares', label: 'Circulares' },
  { value: 'otros', label: 'Otros' }
];

const DocumentsPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    file: null,
    building_id: '',
    category: 'otros'
  });

  useEffect(() => {
    trackModuleVisit('documentos');
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [selectedBuilding, filterCategory]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      if (response.data.length > 0) {
        setUploadForm(prev => ({ ...prev, building_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      let query = '';
      const params = [];
      
      if (selectedBuilding && selectedBuilding !== 'all') {
        params.push(`building_id=${selectedBuilding}`);
      }
      if (filterCategory && filterCategory !== 'all') {
        params.push(`category=${filterCategory}`);
      }
      
      if (params.length > 0) {
        query = '?' + params.join('&');
      }
      
      const response = await axios.get(`${API}/documents${query}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('El archivo excede 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Selecciona un archivo');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('building_id', uploadForm.building_id);
      formData.append('category', uploadForm.category);
      
      await axios.post(`${API}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido exitosamente');
      trackEvent('documento_subido', { 
        building_id: uploadForm.building_id, 
        category: uploadForm.category 
      });
      
      setUploadOpen(false);
      setUploadForm(prev => ({ ...prev, file: null }));
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`);
      const { filename, content_type, file_data } = response.data;
      
      // Convert base64 to blob and download
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/documents/${documentToDelete.id}`);
      setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
      toast.success('Documento eliminado');
      trackEvent('documento_eliminado', { document_id: documentToDelete.id });
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) {
      return <FileText className="h-5 w-5 text-red-500" />;
    } else if (contentType.includes('spreadsheet') || contentType.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    } else {
      return <File className="h-5 w-5 text-blue-500" />;
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      reglamento: 'bg-purple-100 text-purple-700',
      actas: 'bg-blue-100 text-blue-700',
      estados_cuenta: 'bg-green-100 text-green-700',
      circulares: 'bg-orange-100 text-orange-700',
      otros: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      reglamento: 'Reglamento',
      actas: 'Actas',
      estados_cuenta: 'Estados de Cuenta',
      circulares: 'Circulares',
      otros: 'Otros'
    };
    return (
      <Badge className={colors[category] || colors.otros}>
        {labels[category] || category}
      </Badge>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-testid="documents-page-title">
              Documentos
            </h1>
            <p className="text-text-secondary mt-1">Gestión de archivos del conjunto</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Filter by category */}
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="border-0 shadow-none bg-transparent p-0 h-auto w-[140px]" data-testid="filter-category">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Upload button */}
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-accent hover:bg-accent/90 text-white rounded-full"
                  data-testid="upload-document-button"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Subir Documento
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="upload-document-dialog">
                <DialogHeader>
                  <DialogTitle>Subir Documento</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label>Conjunto</Label>
                    <Select 
                      value={uploadForm.building_id} 
                      onValueChange={(val) => setUploadForm({...uploadForm, building_id: val})}
                    >
                      <SelectTrigger data-testid="upload-building-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Categoría</Label>
                    <Select 
                      value={uploadForm.category} 
                      onValueChange={(val) => setUploadForm({...uploadForm, category: val})}
                    >
                      <SelectTrigger data-testid="upload-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Archivo (PDF, Word, Excel - Máx 10MB)</Label>
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                      className="mt-1"
                      data-testid="upload-file-input"
                    />
                    {uploadForm.file && (
                      <p className="text-sm text-slate-500 mt-1">
                        {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                      </p>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-accent hover:bg-accent/90"
                    disabled={uploading || !uploadForm.file}
                    data-testid="upload-submit-button"
                  >
                    {uploading ? 'Subiendo...' : 'Subir Documento'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Documents List */}
        <Card className="overflow-hidden">
          {documents.length === 0 ? (
            <div className="p-12 text-center">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay documentos</p>
              <p className="text-sm text-slate-400 mt-1">Sube el primer documento del conjunto</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {/* Table header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div className="col-span-4">Nombre</div>
                <div className="col-span-2">Categoría</div>
                <div className="col-span-2">Conjunto</div>
                <div className="col-span-2">Subido por</div>
                <div className="col-span-1">Tamaño</div>
                <div className="col-span-1 text-right">Acciones</div>
              </div>
              
              {/* Documents rows */}
              {documents.map((doc) => (
                <div 
                  key={doc.id} 
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 transition-colors items-center"
                  data-testid={`document-row-${doc.id}`}
                >
                  {/* Name */}
                  <div className="col-span-4 flex items-center gap-3">
                    {getFileIcon(doc.content_type)}
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{doc.filename}</p>
                      <p className="text-xs text-slate-400 md:hidden">
                        {new Date(doc.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Category */}
                  <div className="col-span-2 hidden md:block">
                    {getCategoryBadge(doc.category)}
                  </div>
                  
                  {/* Building */}
                  <div className="col-span-2 hidden md:block">
                    <span className="text-sm text-slate-600">{doc.building_name}</span>
                  </div>
                  
                  {/* Uploaded by */}
                  <div className="col-span-2 hidden md:block">
                    <p className="text-sm text-slate-600 truncate">{doc.uploaded_by_name}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(doc.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  
                  {/* Size */}
                  <div className="col-span-1 hidden md:block">
                    <span className="text-sm text-slate-500">{formatFileSize(doc.size)}</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-1 flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                      data-testid={`download-document-${doc.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(doc)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-document-${doc.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Mobile: extra info */}
                  <div className="col-span-full md:hidden flex items-center gap-2 flex-wrap">
                    {getCategoryBadge(doc.category)}
                    <span className="text-xs text-slate-400">{doc.building_name}</span>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs text-slate-400">{formatFileSize(doc.size)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          title="Eliminar Documento"
          description={`¿Estás seguro de eliminar "${documentToDelete?.filename}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
          variant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
